// Original line icons on a 24 px grid (stroke 1.75, round joins). Decorative by default
// (aria-hidden); a meaningful icon-only control gets its label from the control, not the icon.
import { useId, type SVGProps } from "react";

/** A filled crown: the body with three balls and the band under it. */
const CROWN =
  "M2.6 8.9H3.9Q5.3 11.5 6.9 11.4Q8 11.3 8.7 10.4L11.4 6.8H12.6L15.3 10.4Q16 11.3 17.1 11.4Q18.7 11.5 20.1 8.9H21.4L19.95 14Q19.8 14.7 19.1 14.7H4.9Q4.2 14.7 4.05 14ZM4.4 8.4a1.3 1.3 0 1 1-2.6 0a1.3 1.3 0 1 1 2.6 0ZM13.35 6.3a1.35 1.35 0 1 1-2.7 0a1.35 1.35 0 1 1 2.7 0ZM22.2 8.4a1.3 1.3 0 1 1-2.6 0a1.3 1.3 0 1 1 2.6 0ZM5 16.3h14a.9.9 0 0 1 .9.9v.8a.9.9 0 0 1-.9.9H5a.9.9 0 0 1-.9-.9v-.8a.9.9 0 0 1 .9-.9Z";

const paths = {
  check: "M5 12.5l4.5 4.5L19 7.5",
  checkCircle: "M12 3a9 9 0 1 0 0 18a9 9 0 1 0 0-18zM8 12.2l2.8 2.8L16.2 9.6",
  pencil: "M4 20l4.2-1 10.4-10.4a2 2 0 0 0-3.2-3.2L5 15.8zM13.8 6.2l4 4",
  archive: "M3.5 5h17v4h-17zM5 9v10h14V9M10 13h4",
  star: "M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8-4.3-4.1 5.9-.9z",
  dots: "M5 12a1 1 0 1 0 2 0a1 1 0 1 0-2 0zM11 12a1 1 0 1 0 2 0a1 1 0 1 0-2 0zM17 12a1 1 0 1 0 2 0a1 1 0 1 0-2 0z",
  printer: "M7 8V3.5h10V8M7 16.5H4.5V8h15v8.5H17M7 13h10v7.5H7z",
  truck:
    "M3 6h11v10H3zM14 9.5h4l3 3.5v3h-7M7 18m-1.8 0a1.8 1.8 0 1 0 3.6 0a1.8 1.8 0 1 0-3.6 0M17 18m-1.8 0a1.8 1.8 0 1 0 3.6 0a1.8 1.8 0 1 0-3.6 0",
  x: "M6 6l12 12M18 6L6 18",
  dot: "M12 12m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0",
  circle: "M12 12m-6.5 0a6.5 6.5 0 1 0 13 0a6.5 6.5 0 1 0 -13 0",
  arrow: "M5 12h13M13 6.5l5.5 5.5-5.5 5.5",
  triangle: "M12 4.5l8.5 15h-17zM12 10v4.5M12 17.2v.1",
  octagon: "M8.5 3.5h7l5 5v7l-5 5h-7l-5-5v-7zM12 8v5M12 16.2v.1",
  lock: "M7 11V8a5 5 0 0 1 10 0v3M5.5 11h13v9.5h-13zM12 15v2.5",
  clock: "M12 12m-8.5 0a8.5 8.5 0 1 0 17 0a8.5 8.5 0 1 0 -17 0M12 7.5V12l3 2",
  info: "M12 12m-8.5 0a8.5 8.5 0 1 0 17 0a8.5 8.5 0 1 0 -17 0M12 11v5.5M12 7.8v.1",
  sparkle:
    "M12 3.5l2 5.5 5.5 2-5.5 2-2 5.5-2-5.5-5.5-2 5.5-2zM18.5 16.5l.8 2 2 .7-2 .8-.8 2-.7-2-2-.8 2-.7z",
  search: "M10.5 10.5m-6 0a6 6 0 1 0 12 0a6 6 0 1 0 -12 0M15 15l5 5",
  bell: "M6 16.5V11a6 6 0 0 1 12 0v5.5l1.5 2h-15zM10 20.5h4",
  help: "M12 12m-8.5 0a8.5 8.5 0 1 0 17 0a8.5 8.5 0 1 0 -17 0M9.6 9.5a2.5 2.5 0 1 1 3.6 2.3c-.8.4-1.2 1-1.2 1.9M12 16.8v.1",
  menu: "M4 7h16M4 12h16M4 17h16",
  chevronDown: "M6 9.5l6 6 6-6",
  chevronUp: "M6 14.5l6-6 6 6",
  chevronRight: "M9.5 6l6 6-6 6",
  chevronLeft: "M14.5 6l-6 6 6 6",
  plus: "M12 5v14M5 12h14",
  minus: "M5 12h14",
  map: "M3.5 6.5 9 4.5l6 2 5.5-2v13l-5.5 2-6-2-5.5 2zM9 4.5v13M15 6.5v13",
  download: "M12 4v11M7.5 10.5 12 15l4.5-4.5M5 19.5h14",
  trash: "M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v6M14 11v6",
  home: "M4 11l8-6.5 8 6.5M6 9.5v10h12v-10M10 19.5v-5h4v5",
  orders: "M6 3.5h12v17l-3-2-3 2-3-2-3 2zM9 8h6M9 12h6",
  sell: "M4 5h16v10H4zM8 19h8M12 15v4M8 10h3",
  box: "M4 7.5l8-4 8 4v9l-8 4-8-4zM4 7.5l8 4 8-4M12 11.5v9",
  users:
    "M9 9m-3.5 0a3.5 3.5 0 1 0 7 0a3.5 3.5 0 1 0 -7 0M3 20c.5-3.5 3-5.5 6-5.5s5.5 2 6 5.5M16 5.5a3.5 3.5 0 0 1 0 7M18 14.8c1.7.7 2.8 2.5 3 5.2",
  money: "M3.5 7h17v10h-17zM12 12m-2.5 0a2.5 2.5 0 1 0 5 0a2.5 2.5 0 1 0 -5 0M6.5 10v4M17.5 10v4",
  // A banknote with a second note behind it: cash on delivery (owner, 2026-10-21).
  cash: "M3 8h14.5v9H3zM10.25 12.5m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0M5.5 10.5v.01M15 14.5v.01M6.5 5.5H21V14",
  calendar: "M4.5 6h15v14h-15zM4.5 10.5h15M8.5 3.5v4M15.5 3.5v4",
  // Three finder squares and a few modules: pay by QR.
  qr: "M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM6.5 6.5h1v1h-1zM16.5 6.5h1v1h-1zM6.5 16.5h1v1h-1zM14 14h2.5v2.5H14zM17.5 17.5H20V20h-2.5zM14 19.5h1.5M19.5 14v1.5",
  megaphone: "M4 10v4l3 .5V9.5zM7 9.5l10-4.5v14l-10-4.5M9 15l1.5 4.5h2.5L12 15.7",
  store:
    "M4.5 9.5V20h15V9.5M3.5 9.5l2-5h13l2 5c0 1.4-1.1 2.5-2.5 2.5s-2.5-1.1-2.5-2.5c0 1.4-1.1 2.5-2.5 2.5s-2.5-1.1-2.5-2.5c0 1.4-1.1 2.5-2.5 2.5s-2.5-1.1-2.5-2.5zM10 20v-5h4v5",
  chart: "M4 20h16M6.5 17v-5M11 17V7M15.5 17v-8M20 17V4.5",
  grid: "M4.5 4.5h6v6h-6zM13.5 4.5h6v6h-6zM4.5 13.5h6v6h-6zM13.5 13.5h6v6h-6z",
  // An eight-tooth cog around a hub (owner reference, 2026-10-21).
  gear: "M9.32 5.53L9.85 5.34L10.24 2.97L13.76 2.97L14.15 5.34L14.68 5.53L15.19 5.77L17.14 4.37L19.63 6.86L18.23 8.81L18.47 9.32L18.66 9.85L21.03 10.24L21.03 13.76L18.66 14.15L18.47 14.68L18.23 15.19L19.63 17.14L17.14 19.63L15.19 18.23L14.68 18.47L14.15 18.66L13.76 21.03L10.24 21.03L9.85 18.66L9.32 18.47L8.81 18.23L6.86 19.63L4.37 17.14L5.77 15.19L5.53 14.68L5.34 14.15L2.97 13.76L2.97 10.24L5.34 9.85L5.53 9.32L5.77 8.81L4.37 6.86L6.86 4.37L8.81 5.77ZM12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0",
  drawer: "M4 5h16v14H4zM4 13h16M10 16h4",
  pulse: "M3 12h4l2.5-6 4 12 2.5-6H21",
  gauge: "M4.5 17a8 8 0 1 1 15 0M12 13l4-4.5",
  jobs: "M5 6h14M5 12h14M5 18h9",
  sliders: "M6 4v16M12 4v16M18 4v16M4 9h4M10 15h4M16 7h4",
  rocket: "M12 3c3 2 4.5 5 4.5 9l-2 4h-5l-2-4C7.5 8 9 5 12 3zM9.5 16l-2 4M14.5 16l2 4M12 9.5v.1",
  inbox: "M4 13l2.5-8h11L20 13v6.5H4zM4 13h5l1 2.5h4l1-2.5h5",
  terminal: "M4 5h16v14H4zM7.5 9.5l3 2.5-3 2.5M12.5 15h4",
  shield: "M12 3.5l7.5 3v5.5c0 4.5-3.2 7.6-7.5 9-4.3-1.4-7.5-4.5-7.5-9V6.5z",
  lifebuoy:
    "M12 12m-8.5 0a8.5 8.5 0 1 0 17 0a8.5 8.5 0 1 0 -17 0M12 12m-3.5 0a3.5 3.5 0 1 0 7 0a3.5 3.5 0 1 0 -7 0M6 6l3.5 3.5M14.5 14.5L18 18M18 6l-3.5 3.5M9.5 14.5L6 18",
  receipt: "M6 3.5h12v17l-3-2-3 2-3-2-3 2zM9 8h6M9 11.5h6M9 15h3",
  key: "M8.5 15.5m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0M11.5 12.5l8-8M16.5 7.5l2.5 2.5M14.5 9.5l2 2",
  device: "M7 3.5h10v17H7zM11 17.5h2",
  logout: "M14 4.5H5.5v15H14M10 12h10.5M17 8.5l3.5 3.5-3.5 3.5",
  sun: "M12 12m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M5.3 18.7l1.4-1.4M17.3 6.7l1.4-1.4",
  moon: "M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z",
  fingerprint:
    "M8 19c1-2 1.5-4.5 1.5-7a2.5 2.5 0 0 1 5 0c0 1.5-.1 3-.4 4.4M12 12c0 3-.6 5.6-1.8 8M5.5 15.5c.6-1.2 1-2.3 1-3.5a5.5 5.5 0 0 1 9.7-3.5M17.4 11.3c.1.2.1.5.1.7 0 2.6-.4 5-1.2 7.2M4.8 8.5A8 8 0 0 1 19.5 9",
  // One icon per admin area (D-038 sidebar: every row carries an icon).
  // A shopping bag (marketplaces: Daraz).
  bag: "M5 8h14l-1.2 12H6.2zM9 8V6.5a3 3 0 0 1 6 0V8M9.5 11.5v.1M14.5 11.5v.1",
  cart: "M3.5 4.5h2l2.2 10.5h10l1.8-7.5H6.6M9.5 19.5m-1.2 0a1.2 1.2 0 1 0 2.4 0a1.2 1.2 0 1 0-2.4 0M17 19.5m-1.2 0a1.2 1.2 0 1 0 2.4 0a1.2 1.2 0 1 0-2.4 0",
  returnArrow: "M9 14.5L4.5 10 9 5.5M4.5 10H15a4.5 4.5 0 0 1 0 9h-3",
  phoneCheck: "M7 3.5h10v17H7zM11 17.5h2M9.5 10.5l2 2 3.5-3.5",
  tag: "M3.5 12.5V4h8.5l8.5 8.5-8.5 8.5zM8 8.5m-1.2 0a1.2 1.2 0 1 0 2.4 0a1.2 1.2 0 1 0-2.4 0",
  layers: "M12 3.5l8.5 4.5-8.5 4.5-8.5-4.5zM3.5 12l8.5 4.5 8.5-4.5M3.5 16l8.5 4.5 8.5-4.5",
  swap: "M4 8h14l-3.5-3.5M20 16H6l3.5 3.5",
  upload: "M12 15.5V4M7.5 8.5L12 4l4.5 4.5M4.5 15v4.5h15V15",
  image:
    "M4 5h16v14H4zM4 16l4.5-4.5 4 4 2.5-2.5 5 5M15.5 9.5m-1.5 0a1.5 1.5 0 1 0 3 0a1.5 1.5 0 1 0-3 0",
  pie: "M12 3.5V12h8.5A8.5 8.5 0 1 1 12 3.5zM15 3.8A8.5 8.5 0 0 1 20.2 9H15z",
  gift: "M4 9h16v3.5H4zM5.5 12.5V20h13v-7.5M12 9v11M12 9c-1-3-5.5-4-5.5-1.5S10 9 12 9zm0 0c1-3 5.5-4 5.5-1.5S14 9 12 9z",
  chat: "M4 5h16v11H9.5L5 19.5V16H4zM8.5 10.5h.1M12 10.5h.1M15.5 10.5h.1",
  card: "M3 6h18v12H3zM3 10h18M6.5 14.5h4",
  coins:
    "M12 6m-7 0a7 2.5 0 1 0 14 0a7 2.5 0 1 0-14 0M5 6v4c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5V6M5 10v4c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5v-4M5 14v4c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5v-4",
  bank: "M3.5 9.5L12 4.5l8.5 5zM5.5 9.5v8M10 9.5v8M14 9.5v8M18.5 9.5v8M3.5 20h17",
  wallet: "M4 7h14.5a1.5 1.5 0 0 1 1.5 1.5V19H4zM4 7l11-3v3M15.5 13h4.5",
  factory: "M3.5 20V10l5 3V10l5 3V5.5h5V20zM3.5 20h17M8 16.5h2M13 16.5h2",
  file: "M6 3.5h8l4 4v13H6zM14 3.5v4h4M9 12h6M9 15.5h6",
  percent:
    "M6 18L18 6M7.5 7.5m-2 0a2 2 0 1 0 4 0a2 2 0 1 0-4 0M16.5 16.5m-2 0a2 2 0 1 0 4 0a2 2 0 1 0-4 0",
  link: "M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1",
  zap: "M13 3L5 13.5h6L10 21l8-10.5h-6z",
  send: "M4 11.5L20 4l-5.5 16-3.2-6.5zM11.3 13.5L20 4",
  share:
    "M17 6m-2.5 0a2.5 2.5 0 1 0 5 0a2.5 2.5 0 1 0-5 0M7 12m-2.5 0a2.5 2.5 0 1 0 5 0a2.5 2.5 0 1 0-5 0M17 18m-2.5 0a2.5 2.5 0 1 0 5 0a2.5 2.5 0 1 0-5 0M9.2 10.8l5.6-3.6M9.2 13.2l5.6 3.6",
  palette:
    "M12 3.5a8.5 8.5 0 1 0 0 17c1.4 0 2-1 1.5-2.2-.6-1.4.3-2.8 1.8-2.8H18a2.5 2.5 0 0 0 2.5-2.5c0-5.2-3.8-9.5-8.5-9.5zM7.5 11.5h.1M10 7.5h.1M14.5 7.5h.1",
  pages: "M5 4.5h14v15H5zM8 8.5h8M8 12h8M8 15.5h5",
  globe:
    "M12 12m-8.5 0a8.5 8.5 0 1 0 17 0a8.5 8.5 0 1 0-17 0M3.5 12h17M12 3.5c2.5 2.3 3.5 5.3 3.5 8.5s-1 6.2-3.5 8.5c-2.5-2.3-3.5-5.3-3.5-8.5s1-6.2 3.5-8.5z",
  trending: "M3.5 17l6-6 4 4 7-7.5M15 7.5h5.5V13",
  plug: "M9 3.5v4M15 3.5v4M6.5 7.5h11v4a5.5 5.5 0 0 1-11 0zM12 17v3.5",
  code: "M8.5 7.5L4 12l4.5 4.5M15.5 7.5L20 12l-4.5 4.5M13.5 5l-3 14",
  mapPin:
    "M12 21s-6.5-6-6.5-11a6.5 6.5 0 0 1 13 0c0 5-6.5 11-6.5 11zM12 10m-2.2 0a2.2 2.2 0 1 0 4.4 0a2.2 2.2 0 1 0-4.4 0",
  shieldCheck: "M12 3.5l7.5 3v5.5c0 4.5-3.2 7.6-7.5 9-4.3-1.4-7.5-4.5-7.5-9V6.5zM9 12l2 2 4-4",
  scale:
    "M12 4v16M7 20h10M5 7.5h14M5 7.5l-2.5 6a2.5 2.5 0 0 0 5 0zM19 7.5l-2.5 6a2.5 2.5 0 0 0 5 0z",
  flag: "M5 21V4M5 4.5h11l-2 4 2 4H5",
  building: "M5 20.5V4h9v16.5M14 9h5v11.5M3.5 20.5h17M8 8h3M8 11.5h3M8 15h3",
  undo: "M9 13.5 4.5 9 9 4.5M4.5 9H15a5 5 0 0 1 0 10h-3.5",
  redo: "M15 13.5 19.5 9 15 4.5M19.5 9H9a5 5 0 0 0 0 10h3.5",
  monitor: "M3.5 4.5h17v11.5h-17zM8.5 20h7M12 16v4",
  tablet: "M6 3h12v18H6zM11 17.8h2",
  phone: "M8 2.5h8v19H8zM11 18.2h2",
  mail: "M3.5 5.5h17v13h-17zM4 6.5l8 6.5 8-6.5",
  timer:
    "M12 13m-7.5 0a7.5 7.5 0 1 0 15 0a7.5 7.5 0 1 0 -15 0M12 9.5V13l2.5 1.8M9.5 2.5h5M12 2.5v3",
  newspaper:
    "M4 5h12.5v14.5H6.5A2.5 2.5 0 0 1 4 17zM16.5 9H20v8a2.5 2.5 0 0 1-2.5 2.5M7.5 8.5h5.5M7.5 12h5.5M7.5 15.5h3.5",
  wrench:
    "M14.5 4a4.5 4.5 0 0 0-4.2 6.1L4.2 16.2a2 2 0 0 0 2.8 2.8l6.1-6.1A4.5 4.5 0 0 0 19.6 8.6l-2.8 2.8-2.6-.7-.7-2.6 2.8-2.8A4.5 4.5 0 0 0 14.5 4z",
  badge:
    "M12 3l2.3 1.6 2.8-.1.9 2.7 2.2 1.7-.9 2.6.9 2.6-2.2 1.7-.9 2.7-2.8-.1L12 21l-2.3-1.6-2.8.1-.9-2.7-2.2-1.7.9-2.6-.9-2.6 2.2-1.7.9-2.7 2.8.1zM8.8 12l2.2 2.2 4.2-4.4",
  eye: "M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12zM12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0",
  layout: "M3.5 4h17v16h-17zM3.5 9h17M9.5 9v11",
  folder: "M3.5 7a2 2 0 0 1 2-2h4l2 2.5h7a2 2 0 0 1 2 2V17a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2z",
  list: "M4.5 6.5h.1M4.5 12h.1M4.5 17.5h.1M8.5 6.5h11M8.5 12h11M8.5 17.5h11",
  target:
    "M12 12m-8.5 0a8.5 8.5 0 1 0 17 0a8.5 8.5 0 1 0 -17 0M12 12m-4.5 0a4.5 4.5 0 1 0 9 0a4.5 4.5 0 1 0 -9 0M12 12h.1",
  ship: "M3 14.5h18l-2.2 4.5H5.2zM5.5 14.5V10h13v4.5M9 10V6.5h6V10M12 3.5v3",
  graduation:
    "M2.5 9 12 4.5 21.5 9 12 13.5zM6.5 11v4.5c0 1.4 2.5 3 5.5 3s5.5-1.6 5.5-3V11M21.5 9v5",
  route:
    "M6 19.5m-2.5 0a2.5 2.5 0 1 0 5 0a2.5 2.5 0 1 0 -5 0M18 5.5m-2.5 0a2.5 2.5 0 1 0 5 0a2.5 2.5 0 1 0 -5 0M8.5 19.5H15a3.5 3.5 0 0 0 0-7H9a3.5 3.5 0 0 1 0-7h6.5",
  video: "M3.5 6.5h12v11h-12zM15.5 10.5l5-3v9l-5-3",
  play: "M8.5 5.5v13l10-6.5z",
  infinity: "M7.5 9a3 3 0 1 0 0 6c2.5 0 6.5-6 9-6a3 3 0 1 1 0 6c-2.5 0-6.5-6-9-6z",
  // Plans & pricing (D-065): icons the owner can give a plan.
  crown: "M4 8l4 3.5L12 5l4 6.5L20 8l-1.5 10h-13zM5.5 20.5h13",
  gem: "M7 4h10l3.5 5L12 20.5 3.5 9zM3.5 9h17M9.5 4L12 9l2.5-5M12 9v11.5",
  trophy:
    "M8 4h8v5a4 4 0 0 1-8 0zM8 6H4.5v1.5A3.5 3.5 0 0 0 8 11M16 6h3.5v1.5A3.5 3.5 0 0 1 16 11M12 13v4M8.5 20.5h7l-1-3.5h-5z",
  leaf: "M5 19c0-8 5-13.5 14.5-14-.3 9.5-5.8 14.5-13.7 14.5M5 19c3-4 6-6.5 9.5-8.5",
  flame:
    "M12 3c.5 3.5 5.5 6 5.5 11a5.5 5.5 0 0 1-11 0c0-2.5 1.2-4.2 2.5-5.5.2 2 1 3 2 3.5 0-3 .3-6 1-9z",
  heart:
    "M12 19.5s-7.5-4.6-7.5-10a4.2 4.2 0 0 1 7.5-2.6 4.2 4.2 0 0 1 7.5 2.6c0 5.4-7.5 10-7.5 10z",
  // Collections (1024): drag handle, hidden, open in a new tab.
  grip: "M8.5 6a1 1 0 1 0 2 0a1 1 0 1 0-2 0zM13.5 6a1 1 0 1 0 2 0a1 1 0 1 0-2 0zM8.5 12a1 1 0 1 0 2 0a1 1 0 1 0-2 0zM13.5 12a1 1 0 1 0 2 0a1 1 0 1 0-2 0zM8.5 18a1 1 0 1 0 2 0a1 1 0 1 0-2 0zM13.5 18a1 1 0 1 0 2 0a1 1 0 1 0-2 0z",
  eyeOff:
    "M4 4l16 16M10.6 6A9.6 9.6 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a17 17 0 0 1-2.6 3.4M6.5 7.4A17 17 0 0 0 2.5 12S6 18.5 12 18.5a9 9 0 0 0 4.6-1.3M9.9 9.9a3 3 0 0 0 4.2 4.2",
  external: "M13.5 4.5h6v6M19.5 4.5L11 13M17 14v5.5H4.5V7H10",
  // Product row menu: duplicate, update status.
  copy: "M8.5 8.5h11v11h-11zM15.5 8.5V4.5h-11v11h4",
  refresh: "M19.5 12a7.5 7.5 0 1 1-2.2-5.3M19.5 4.5v4h-4",
  eyedropper:
    "M14.5 6.5l3 3M16.2 4.3a2.1 2.1 0 0 1 3 3L17.5 9l-2.5-2.5zM15 7.8l-8.6 8.6-.9 3.1 3.1-.9 8.6-8.6",
  // Rich-text toolbar
  bold: "M7 4.5h6a3.75 3.75 0 0 1 0 7.5H7zM7 12h7a3.75 3.75 0 0 1 0 7.5H7z",
  italic: "M10 4.5h8M6 19.5h8M14.5 4.5l-5 15",
  underline: "M7 4v7a5 5 0 0 0 10 0V4M5 20h14",
  strike:
    "M16.5 7.5c-.6-1.8-2.4-3-4.5-3-2.5 0-4.5 1.5-4.5 3.5 0 1.7 1.2 2.6 3 3.2M4.5 12h15M8 16.2c.6 2 2.4 3.3 4.5 3.3 2.6 0 4.5-1.5 4.5-3.6 0-.9-.3-1.6-.9-2.1",
  subscript: "M4 6l7 9M11 6l-7 9M16.5 15.5a1.75 1.75 0 0 1 3.5.2c0 1.2-3.5 2.3-3.5 4.3h3.5",
  superscript: "M4 9l7 9M11 9l-7 9M16.5 5.5a1.75 1.75 0 0 1 3.5.2c0 1.2-3.5 2.3-3.5 4.3h3.5",
  textColor: "M6.5 16L12 4l5.5 12M8.3 12h7.4M4 20h16",
  highlight: "M14.5 4.5l5 5-8.5 8.5H6v-5zM4 21h16",
  alignLeft: "M4 6h16M4 10h10M4 14h16M4 18h10",
  alignCenter: "M4 6h16M7 10h10M4 14h16M7 18h10",
  alignRight: "M4 6h16M10 10h10M4 14h16M10 18h10",
  alignJustify: "M4 6h16M4 10h16M4 14h16M4 18h16",
  listOrdered:
    "M10 6.5h10M10 12h10M10 17.5h10M4 4.5l1.5-1v5M4 14.2a1.5 1.5 0 0 1 3 .3c0 1-3 2.2-3 3.7h3",
  indent: "M4 5h16M11 10h9M11 14h9M4 19h16M4 9.5l3 2.5-3 2.5",
  outdent: "M4 5h16M11 10h9M11 14h9M4 19h16M7 9.5l-3 2.5 3 2.5",
  quote: "M5 18c0-4.5 1.5-7.5 4.5-9.5M5 13h4v5H5zM13.5 18c0-4.5 1.5-7.5 4.5-9.5M13.5 13h4v5h-4z",
  table: "M3.5 5h17v14h-17zM3.5 10h17M3.5 14.5h17M9.5 10v9M15 10v9",
  divider: "M3.5 12h17M7 6.5h10M7 17.5h10",
  eraser:
    "M8.5 20h11M4.7 14.3l9.6-9.6a2 2 0 0 1 2.8 0l2.2 2.2a2 2 0 0 1 0 2.8l-7.7 7.7a2 2 0 0 1-1.4.6H8.3a2 2 0 0 1-1.4-.6l-2.2-2.2a.9.9 0 0 1 0-.9zM9.5 9.5l5 5",
  unlink:
    "M9 15l-1.5 1.5a3 3 0 0 1-4.2-4.2L5 10.5M15 9l1.5-1.5a3 3 0 0 1 4.2 4.2L19 13.5M4 4l16 16",
  // The plan crowns (owner, 2026-10-27): one solid crown, Startup in the logo green, Business in the
  // logo blue (SOLID below). Plans show them, and a locked feature shows the plan that unlocks it.
  crownStartup: CROWN,
  crownBusiness: CROWN,
} as const;

export type IconName = keyof typeof paths;

/** Solid icons with their own colour: no stroke, and neither `gradient` nor a hover repaints them. */
const SOLID: Partial<Record<IconName, string>> = {
  crownStartup: "var(--ady-color-brand-green)",
  crownBusiness: "var(--ady-color-brand-blue)",
};

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, "name"> {
  name: IconName;
  size?: number;
  /** Paint the icon with the logo gradient, blue → green (D-039: active and brand icons). */
  gradient?: boolean;
}

export function Icon({ name, size, gradient = false, style, ...rest }: IconProps) {
  const filled = name === "dot";
  const gid = `ady-ig${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const paint = gradient ? `url(#${gid})` : "currentColor";
  // D-042: without an explicit size the icon follows --ady-icon-size (16 px on the admin desktop).
  // --ady-icon-grad lets `.ady-hover-brand:hover` paint a resting icon with this icon's gradient.
  const grad = { "--ady-icon-grad": `url(#${gid})` } as Record<string, string>;
  const sized =
    size === undefined
      ? {
          style: {
            width: "var(--ady-icon-size)",
            height: "var(--ady-icon-size)",
            ...grad,
            ...style,
          },
        }
      : { width: size, height: size, style: { ...grad, ...style } };
  const solid = SOLID[name];
  if (solid)
    return (
      <svg
        {...sized}
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
        {...rest}
        className={rest.className ? `ady-icon ${rest.className}` : "ady-icon"}
      >
        {/* Inline styles, so `.ady-hover-brand:hover .ady-icon > path` adds no outline. A filled
            control sets --ady-icon-solid to its label colour (components.css). */}
        <path d={paths[name]} style={{ fill: `var(--ady-icon-solid, ${solid})`, stroke: "none" }} />
      </svg>
    );
  return (
    <svg
      {...sized}
      viewBox="0 0 24 24"
      fill={filled ? paint : "none"}
      stroke={filled ? "none" : paint}
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
      className={rest.className ? `ady-icon ${rest.className}` : "ady-icon"}
    >
      {/* Always defined, so a hover can switch to the gradient without a re-render. */}
      {gradient || !filled ? (
        <defs>
          <linearGradient id={gid} gradientUnits="userSpaceOnUse" x1="2" y1="2" x2="22" y2="22">
            <stop offset="0" stopColor="var(--ady-color-brand-blue)" />
            <stop offset="1" stopColor="var(--ady-color-brand-green)" />
          </linearGradient>
        </defs>
      ) : null}
      <path d={paths[name]} />
    </svg>
  );
}
